// src/components/LandingView.tsx
//
// Processor-close copy: FDW finishes Marqeta/Lithic for the night.
// Customer posts the ledger and pays the bank. Sample numbers are
// the three-swipe walkthrough (cleared $80 / net $79.60), labeled.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from './SEO';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { HeroStream } from './landing/HeroStream';
import { ReframeFlow } from './landing/ReframeFlow';

export const LandingView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Five Dollar Workday | AI that closes your card program"
        description="FDW finishes the Marqeta or Lithic night. You get a pack: what settled, what to fund, what needs a person. You post it and pay it."
        canonical="https://fivedollarworkday.com"
      />

      <main className="relative min-h-screen bg-brand-surface text-brand-black font-sans selection:bg-brand-accent selection:text-white pt-16">

        {/* ── 1. HERO ─────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-16 md:py-20 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto flex flex-col gap-10">

            <div className="flex flex-col gap-6 max-w-3xl">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.08]">
                AI that closes your card program.
              </h1>
              <p className="text-lg text-brand-charcoal font-light leading-relaxed max-w-xl">
                FDW finishes the Marqeta or Lithic night. You get a pack: what settled,
                what to fund, what needs a person. You post it and pay it.
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/signup?next=/chat&intent=program-map')}
                  className="w-fit"
                >
                  Get a program map
                </Button>
                <p className="text-xs text-brand-gray font-light">
                  Make an account. Tell us which processor. The chat books the 30 minutes.
                </p>
              </div>
            </div>

            <div className="w-full flex justify-center">
              <HeroStream />
            </div>

          </div>
        </section>

        {/* ── 2. WHAT THIS IS ─────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-20 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray">
                What this is
              </p>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-black leading-tight">
                The work someone on finance does after the day's card activity stops — every
                night, without being asked.
              </h2>
              <p className="text-brand-charcoal text-lg leading-relaxed font-light">
                You're not buying seats. You're buying a finished check: matched, or named and
                waiting for you.
              </p>
            </div>
          </div>
        </section>

        {/* ── 3. WORKS WITH THE STACK YOU HAVE ────────────────────── */}
        <section className="px-6 md:px-12 py-16 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-4">
              <h2 className="text-2xl font-semibold tracking-tight text-brand-black">
                Works with the processor you already have.
              </h2>
              <p className="text-brand-charcoal text-base leading-relaxed font-light">
                Marqeta or Lithic. You keep the ledger and the bank. Nothing to migrate.
              </p>
              <div className="flex flex-wrap gap-2 text-xs text-brand-charcoal font-mono mt-2">
                <span>Marqeta</span>
                <span className="text-brand-border">·</span>
                <span>Lithic</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. HOW IT WORKS ─────────────────────────────────────── */}
        <section id="how-it-works" className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black leading-tight">
                It finishes the check.<br />You handle the leftovers.
              </h2>
              <div className="space-y-4">
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  Your processor knows what it approved and what settled. It does not explain
                  the difference — or the charges that posted with no approval.
                </p>
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  Each morning you get a pack: what cleared, the net the processor will move,
                  and the short list. You book your ledger and tick the bank.
                </p>
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  You handle the leftovers. That's the job.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6">
              <ReframeFlow />
            </div>

          </div>
        </section>

        {/* ── 5. PRODUCT / NIGHT RUN ──────────────────────────────── */}
        <section id="night-run" className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-10 max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                Product
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black">
                It finishes the check and hands you the leftovers.
              </h2>
              <p className="text-brand-charcoal text-sm font-light mt-1">
                Daily processor close · March 28
              </p>
            </div>

            <Card className="border border-brand-border rounded-2xl bg-white max-w-lg">
              <CardContent className="p-6 flex flex-col gap-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray">
                  Sample run — not a live account
                </p>
                <div className="font-mono text-sm">
                  <p className="text-brand-black">Cleared</p>
                  <p className="text-brand-gray text-xs pl-4">$80.00 · two matched, one with no approval</p>
                </div>
                <div className="font-mono text-sm">
                  <p className="text-brand-black">Net the processor will move</p>
                  <p className="text-brand-gray text-xs pl-4">$79.60 · sales are not the wire</p>
                </div>
                <div className="font-mono text-sm">
                  <p className="text-brand-accent">Needs a person · 2 rows</p>
                  <p className="text-brand-gray text-xs pl-4">Force post $10.00 · aging approval $18.00</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── 6. SECURITY / TRUST ──────────────────────────────────── */}
        <section id="security" className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-4">
              <h2 className="text-2xl font-semibold tracking-tight text-brand-black">
                We don't train models on your program data or card numbers.
              </h2>
              <p className="text-brand-charcoal text-base leading-relaxed font-light">
                We read the processor. We don't hold card numbers. We don't touch your ledger
                or your bank.
              </p>
              <p className="text-brand-gray text-sm font-light mt-2">
                Security notes on request.
              </p>
            </div>
          </div>
        </section>

        {/* ── 7. CLOSING CTA ───────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-24">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-6">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-black">
                Give finance the morning back.
              </h2>
              <p className="text-brand-charcoal text-base font-light">
                We'll map your Marqeta or Lithic program and show what the pack would have
                said last week. You keep the books.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/signup?next=/chat&intent=program-map')}
                  className="w-fit"
                >
                  Get a program map
                </Button>
                <p className="text-xs text-brand-gray font-light">
                  Make an account. The chat books 30 minutes.
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};